"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { PenLine, ShieldCheck, Signature, Sparkles } from "lucide-react";
import { MockChrome } from "./MockWindow";

/**
 * Auto-looping audit-trail demo. New entries fade in from the top one
 * after another, with a subtle indicator dot, and older entries shift
 * down in unison — like a live audit log. Loops indefinitely.
 */

type Entry = {
  time: string;
  initials: string;
  action: string;
  field: string;
  from?: string;
  to?: string;
  kind: "edit" | "sign" | "lock" | "system";
};

const ENTRIES: Entry[] = [
  { time: "14:32", initials: "MK", action: "Updated", field: "AE.Severity", from: "Moderate", to: "Severe", kind: "edit" },
  { time: "14:28", initials: "JT", action: "Added", field: "AE.Start date", to: "2025-04-14", kind: "edit" },
  { time: "14:24", initials: "MK", action: "Resolved query", field: "AE.Verbatim term", kind: "edit" },
  { time: "13:55", initials: "•", action: "Auto edit-check", field: "AE.Outcome", to: "Required", kind: "system" },
  { time: "11:02", initials: "LC", action: "Signed", field: "Visit 2 lock", kind: "sign" },
];

function ActionIcon({ kind }: { kind: Entry["kind"] }) {
  if (kind === "sign") return <Signature className="h-2.5 w-2.5 text-success" />;
  if (kind === "system") return <Sparkles className="h-2.5 w-2.5 text-primary" />;
  return <PenLine className="h-2.5 w-2.5 text-muted-foreground" />;
}

export function AuditTrailShowcase() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const ctx = gsap.context(() => {
      // Each entry starts hidden; reveal sequentially with stagger
      gsap.set(".audit-entry", { opacity: 0, y: -8 });

      const tl = gsap.timeline({ repeat: -1, repeatDelay: 2.5, defaults: { ease: "power2.out" } });

      // Reveal one-by-one
      ENTRIES.forEach((_, i) => {
        tl.to(`.audit-entry-${i}`, { opacity: 1, y: 0, duration: 0.35 }, `+=${i === 0 ? 0.2 : 0.7}`);
        // Highlight the "Immutable" badge briefly when an entry lands
        if (i === 0) {
          tl.fromTo(
            ".immutable-pulse",
            { scale: 1 },
            { scale: 1.12, duration: 0.15, yoyo: true, repeat: 1 },
            "<+=0.05",
          );
        }
      });

      // Reset
      tl.to(".audit-entry", { opacity: 0, y: -8, duration: 0.4 }, "+=1.6");
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={root}
      className="overflow-hidden rounded-2xl border border-border/60 bg-surface shadow-[var(--shadow-card)]"
      role="img"
      aria-label="Animated audit trail demo: log entries appear in sequence, with an Immutable badge that pulses when each entry lands"
    >
      <MockChrome title="Audit trail · SUBJ-00482" />

      {/* Content scrolls at very narrow widths rather than being cut off.
          The scroll box is the content, not the root, so the root keeps
          clipping the rounded corners and the animation layer. */}
      <div className="overflow-x-auto p-4 text-[11px]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-semibold text-foreground">Audit trail</span>
            <span className="font-mono text-[9px] text-muted-foreground">SUBJ-00482</span>
          </div>
          <span className="immutable-pulse inline-flex items-center gap-1 rounded-full bg-success/10 px-1.5 py-px text-[9px] font-semibold text-success">
            <ShieldCheck className="h-2.5 w-2.5" />
            Immutable
          </span>
        </div>

        <div className="mt-3 space-y-1">
          {ENTRIES.map((r, i) => (
            <div
              key={i}
              className={`audit-entry audit-entry-${i} flex items-start gap-2 rounded-md border border-border/60 bg-surface px-2 py-1.5`}
            >
              <span className="mt-px font-mono text-[9px] text-muted-foreground">{r.time}</span>
              <span className="mt-px grid h-4 w-4 shrink-0 place-items-center rounded-full border border-border/60 bg-surface-muted text-[8px] font-bold text-foreground/70">
                {r.initials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <ActionIcon kind={r.kind} />
                  <span className="font-semibold text-foreground">{r.action}</span>
                  <span className="truncate font-mono text-muted-foreground">{r.field}</span>
                </div>
                {(r.from || r.to) && (
                  <div className="mt-0.5 ml-4 flex flex-wrap items-center gap-1.5 text-[9px]">
                    {r.from && (
                      <span className="rounded border border-border/60 bg-surface-muted px-1 py-px font-mono text-muted-foreground line-through">
                        {r.from}
                      </span>
                    )}
                    {r.to && (
                      <>
                        {r.from && <span className="text-muted-foreground/60">→</span>}
                        <span className="rounded border border-success/30 bg-success/10 px-1 py-px font-mono text-success">
                          {r.to}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between rounded-md border border-border/60 bg-surface-muted/60 px-2 py-1.5 text-[9px] text-muted-foreground">
          <span className="font-semibold uppercase tracking-wider">21 CFR Part 11 · ICH E6(R3)</span>
          <span className="font-mono">Page 1 of 47</span>
        </div>
      </div>
    </div>
  );
}
